import React, { useState, useEffect, useContext, Suspense, lazy } from "react";
import logo from "../assets/01pos.webp";
import { useNavigate } from "react-router";
import { notify } from "../components/toastify";
import loader from "../assets/loader.webp";
import useRotatingImages from "../utility/useRotatingImages";
import CalculateIcon from "@mui/icons-material/Calculate";
import Calculator from "../utility/Calculator";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  adminAxios,
  managerAxios,
  cashierAxios,
  supervisorAxios,
} from "../axiosInstance";
import { ProfileDetailsContext } from "../context/ProfileDetailsProvider";

// Dynamically import PaystackButton using React.lazy for React apps
const PaystackButton = lazy(() =>
  import("react-paystack").then((mod) => ({ default: mod.PaystackButton })),
);

function Renew() {
  const { profileDetails } = useContext(ProfileDetailsContext);
  const navigate = useNavigate();
  const currentImage = useRotatingImages(10000);
  const [calculator, setCalculator] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  // For plan upgrade/renew popup/modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(""); // only "pro" allowed upgrade, any for renew
  const [pendingAmount, setPendingAmount] = useState(0);
  const [paystackRef, setPaystackRef] = useState("");
  const [paystackLoading, setPaystackLoading] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);

  // Renewal: number of months selector
  const [renewMonths, setRenewMonths] = useState(1);
  const [upgradeMonths, setUpgradeMonths] = useState(1);

  // .env
  const PAYSTACK_PUBLIC_KEY =
    "pk_live_05e106e852260673bf438c30da7cb06558b0630e";
  // //"pk_live_05e106e852260673bf438c30da7cb06558b0630e";

  // Plan configs
  const planConfigs = {
    standard: {
      name: "Standard",
      amount: 5000,
    },
    pro: {
      name: "Pro",
      amount: 10000,
    },
  };

  // Only allow upgrade to pro
  const ALLOWED_UPGRADE_PLAN = "pro";

  // Utility for renewal: get active plan or fallback
  const getCurrentPlan = () => {
    if (profileData && profileData.plan) {
      const plan = profileData.plan.toLowerCase();
      if (planConfigs[plan]) return plan;
    }
    // Fallback to standard if no plan is set
    return "standard";
  };

  const getUserEmail = () => profileData?.email || profileDetails?.email || "";
  const getUserName = () =>
    `${profileData?.firstname || ""} ${profileData?.lastname || ""}`.trim();
  const getUserPhone = () => profileData?.phoneno || "";

  // Get user role from sessionStorage
  const getUserRole = () => {
    try {
      const token = sessionStorage.getItem("token");
      if (token) {
        const { role } = JSON.parse(token);
        return role;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
    }
    return null;
  };

  // Get the appropriate axios instance and endpoint based on role
  const getAxiosInstanceAndEndpoint = (role) => {
    switch (role) {
      case "Admin":
        return {
          axiosInstance: adminAxios,
          profileEndpoint: "/users/profile",
          billingEndpoint: "billing/upgrade",
        };
      case "Manager":
        return {
          axiosInstance: managerAxios,
          profileEndpoint: "/users/profile",
          billingEndpoint: "billing/upgrade",
        };
      case "Cashier":
        return {
          axiosInstance: cashierAxios,
          profileEndpoint: "/users/profile",
          billingEndpoint: "billing/upgrade",
        };
      case "Supervisor":
        return {
          axiosInstance: supervisorAxios,
          profileEndpoint: "user-management/profile",
          billingEndpoint: "billing/upgrade",
        };
      default:
        return {
          axiosInstance: adminAxios,
          profileEndpoint: "/users/profile",
          billingEndpoint: "billing/upgrade",
        };
    }
  };

  // Fetch profile data
  const fetchData = async () => {
    try {
      // Get role from sessionStorage
      const role = getUserRole();
      if (!role) {
        notify("Please login to continue", "error");
        navigate("/login");
        return;
      }

      setUserRole(role);

      // Get the appropriate axios instance and endpoint
      const { axiosInstance, profileEndpoint } =
        getAxiosInstanceAndEndpoint(role);

      console.log(
        `[SUBSCRIPTION] Fetching ${role} profile from: GET ${profileEndpoint}`,
      );

      // Fetch profile data using the correct axios instance
      const response = await axiosInstance.get(profileEndpoint);
      const data = response?.data?.data;
      setProfileData(data);

      // If plan is not expired, optionally redirect to appropriate dashboard
      // (This allows users to still upgrade if they manually navigate here)
      // Uncomment the following if you want to force redirect non-expired users:
      // if (data && !data.is_plan_expired) {
      //   const dashboardRoutes = {
      //     Admin: "/admin",
      //     Manager: "/manager",
      //     Cashier: "/cashier",
      //     Supervisor: "/supervisordashboard",
      //   };
      //   notify("Your plan is active. Redirecting to dashboard...", "success");
      //   setTimeout(() => navigate(dashboardRoutes[role] || "/login"), 2000);
      // }
    } catch (err) {
      console.error("[SUBSCRIPTION] Error fetching profile data", err);
      console.error(
        "[SUBSCRIPTION] Profile error response:",
        err?.response?.data,
      );
      if (err?.response?.status === 401) {
        notify("Session expired. Please login again", "error");
        navigate("/login");
      } else {
        notify(
          err?.response?.data?.message || "Failed to load profile data",
          "error",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set the pending amount for renewal/upgrades
  useEffect(() => {
    if (!selectedPlan || !planConfigs[selectedPlan]) {
      return;
    }

    if (isRenewal && renewMonths) {
      const amount = planConfigs[selectedPlan].amount * renewMonths;
      setPendingAmount(amount);
      setPaystackRef(`Renew-${selectedPlan}-${renewMonths}M-${Date.now()}`);
    } else if (!isRenewal && upgradeMonths) {
      const amount = planConfigs[selectedPlan].amount * upgradeMonths;
      setPendingAmount(amount);
      setPaystackRef(`Upgrade-${selectedPlan}-${upgradeMonths}M-${Date.now()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenewal, selectedPlan, renewMonths, upgradeMonths]);

  const paystackConfig = {
    reference:
      paystackRef || `Renew-${isRenewal ? "Renewal" : "Upgrade"}-${Date.now()}`,
    email: getUserEmail(),
    amount: (pendingAmount || 1000) * 100, // Paystack expects kobo
    publicKey: PAYSTACK_PUBLIC_KEY,
    currency: "NGN",
    metadata: {
      custom_fields: [
        {
          display_name: getUserName(),
          variable_name: "phone_number",
          value: getUserPhone(),
        },
      ],
      upgrade_plan: selectedPlan,
      user_id: profileData?.id,
      renewal: isRenewal ? true : false,
      renewal_months: isRenewal ? renewMonths : undefined,
      upgrade_months: !isRenewal ? upgradeMonths : undefined,
    },
  };

  // Function to call the upgrade endpoint (all roles can upgrade)
  const upgradePlan = async (
    amount,
    months,
    plan,
    status = "success",
    reference,
  ) => {
    try {
      // Get the appropriate axios instance and billing endpoint based on role
      // Use userRole state if available, otherwise get from sessionStorage
      const role = userRole || getUserRole();
      if (!role) {
        throw new Error("User role not found");
      }

      const { axiosInstance, billingEndpoint } =
        getAxiosInstanceAndEndpoint(role);
      const payload = {
        amount,
        months,
        plan,
        status,
        reference,
        payment_provider: "paystack",
      };

      const response = await axiosInstance.post(billingEndpoint, payload);

      return response.data;
    } catch (error) {
      console.error("[SUBSCRIPTION] Error upgrading plan:", error);
      console.error(
        "[SUBSCRIPTION] Billing error response:",
        error?.response?.data,
      );
      throw error;
    }
  };

  // Handle the paystack payment success
  const handlePaystackSuccess = async (reference) => {
    setPaystackLoading(true);
    try {
      // Get the months and amount for the selected plan
      const months = isRenewal ? renewMonths : upgradeMonths;
      const amount = selectedPlanTotalAmount;
      const plan = selectedPlan;
      console.log("[SUBSCRIPTION] Is renewal:", isRenewal);

      if (!plan || !amount || !months) {
        throw new Error("Missing required plan information");
      }

      // Call the upgrade endpoint to update the subscription
      await upgradePlan(
        amount,
        months,
        plan,
        "success",
        reference?.reference || reference,
      );

      // Show success message
      let planName = planConfigs[selectedPlan]?.name || "Selected";
      let msg = `Payment successful for ${planName} plan`;
      if (isRenewal)
        msg += ` (Renewal for ${renewMonths} ${renewMonths === 1 ? "month" : "months"})`;
      else
        msg += ` (Upgrade for ${upgradeMonths} ${upgradeMonths === 1 ? "month" : "months"})`;

      notify(`${msg}! Reference: ${reference.reference}`, "success");

      // Reset state and close modal only on success
      setShowUpgradeModal(false);
      setSelectedPlan("");
      setIsRenewal(false);
      setRenewMonths(1);
      setUpgradeMonths(1);

      // Redirect to appropriate dashboard after successful payment
      const dashboardRoutes = {
        Admin: "/admin",
        Manager: "/manager",
        Cashier: "/cashier",
        Supervisor: "/supervisordashboard",
      };
      const redirectRoute = dashboardRoutes[userRole] || "/login";
      setTimeout(() => {
        navigate(redirectRoute);
      }, 2000);
    } catch (err) {
      console.error("[SUBSCRIPTION] Error processing upgrade:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Payment received but processing failed. Please contact support with the payment reference.";
      notify(errorMessage, "error");
      // Keep modal open on error so user can see the error message
    } finally {
      setPaystackLoading(false);
    }
  };

  const handlePaystackClose = () => {
    setPaystackLoading(false);
    setShowUpgradeModal(false);
    setIsRenewal(false);
    setRenewMonths(1);
    setUpgradeMonths(1);
  };

  // Handle renew click, set plan & show modal with months select (all roles can renew)
  const onRenewClick = () => {
    let plan = getCurrentPlan();
    setShowUpgradeModal(true);
    setSelectedPlan(plan);
    setRenewMonths(1);
    setPendingAmount(planConfigs[plan].amount);
    setPaystackRef(""); // will reset in useEffect
    setIsRenewal(true);
    setUpgradeMonths(1);
  };

  // Determine current plan name from profileData
  const currentPlan = getCurrentPlan();
  const planDisplayName = planConfigs[currentPlan]?.name || "Standard";

  const selectedPlanAmount =
    selectedPlan && planConfigs[selectedPlan]
      ? planConfigs[selectedPlan].amount
      : 0;
  const selectedPlanMonths = isRenewal ? renewMonths : upgradeMonths;
  const selectedPlanTotalAmount = selectedPlanAmount * selectedPlanMonths;

  // Expiry date display: use plan_expires_at from API, fallback to expiry, then to default
  let expiryDisplay = "Not set";
  if (profileData) {
    // Try plan_expires_at first (new API field)
    const expiryDateString = profileData.plan_expires_at || profileData.expiry;
    if (expiryDateString) {
      try {
        const expiryDate = new Date(expiryDateString);
        if (!isNaN(expiryDate.getTime())) {
          expiryDisplay = expiryDate.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }
      } catch (_) {
        // If date parsing fails, keep default
      }
    }
  }

  // Get company/user name for greeting
  const companyName =
    profileData?.firstname ||
    profileData?.company_name ||
    profileDetails?.user?.firstname ||
    "User";

  if (isLoading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <img src={loader} alt="loader" className="w-10 lg:w-20" />
      </div>
    );
  }

  return (
    <div
      className="relative w-full min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${currentImage})` }}
    >
      {calculator && <Calculator handleClose={setCalculator} />}
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black opacity-20 z-10"></div>
      <div className="relative z-20 min-h-screen py-8 px-4">
        <div className="flex justify-center items-start">
          <div className="flex flex-col gap-6 px-6 py-8 w-full max-w-4xl bg-white rounded-lg shadow-xl">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <img src={logo} alt="logo" className="w-24" />
              </div>
              <CalculateIcon
                sx={{ fontSize: "50px" }}
                onClick={() => setCalculator(true)}
                className="cursor-pointer"
              />
            </div>

            {/* Warning Message */}
            {profileData?.is_plan_expired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="flex justify-center mb-2">
                  <WarningAmberIcon
                    sx={{ fontSize: "60px" }}
                    className="text-red-500"
                  />
                </div>
                <h2 className="text-2xl font-semibold text-red-800 mb-2">
                  License Expired
                </h2>
                <p className="text-red-700">
                  Hello <span className="font-bold text-lg">{companyName}</span>
                  , your subscription has expired. Please renew or upgrade your
                  plan to continue using the service.
                </p>
              </div>
            )}

            {/* Subscription Summary - Same as Profile.jsx */}
            <div className="w-full flex justify-center">
              <div className="bg-transparent border border-gray-200 rounded-2xl w-full p-8 flex flex-col items-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center tracking-tight">
                  Subscription Details
                </h2>
                <div className="w-full flex flex-col md:flex-row gap-0 px-2 md:px-8 border-y border-gray-100">
                  <div className="flex-1 flex flex-col items-center py-7 md:border-r border-gray-100">
                    <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                      Plan
                    </span>
                    <span className="bg-blue-100 text-blue-900 text-lg px-6 py-2 rounded-full font-medium border border-blue-200 shadow">
                      {planDisplayName}
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col items-center py-7">
                    <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                      Expiry
                    </span>
                    <span className="text-base font-bold text-gray-700">
                      {expiryDisplay}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-3 mt-9 w-full px-2 md:px-8">
                  <button
                    className="flex-1 py-2 md:py-3 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white shadow-sm transition duration-150 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-green-300"
                    style={{ letterSpacing: ".04em" }}
                    onClick={onRenewClick}
                  >
                    Renew
                  </button>
                  {/*<button
                    className="flex-1 py-2 md:py-3 rounded-lg font-bold bg-blue-700 hover:bg-blue-800 text-white shadow-sm transition duration-150 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    style={{ letterSpacing: ".04em" }}
                    onClick={onUpgradeClick}
                  >
                    Upgrade to Pro
                  </button>*/}
                </div>
              </div>
            </div>

            {/* Upgrade/Renew Modal/Popup - Available for all users */}
            {showUpgradeModal && (
              <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60">
                <div className="bg-white rounded-lg shadow-2xl p-8 w-[95vw] max-w-md animate-fadeIn relative">
                  <button
                    onClick={handlePaystackClose}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-3xl"
                    aria-label="Close"
                  >
                    &times;
                  </button>
                  <h3 className="text-2xl font-bold mb-6 text-center text-blue-900">
                    {isRenewal ? "Renew Your Plan" : "Upgrade to Pro"}
                  </h3>
                  {!isRenewal ? (
                    <div className="flex flex-col items-center my-2 gap-3 w-full">
                      <div className="flex flex-col border rounded-lg px-5 py-4 bg-indigo-50 border-blue-300 w-full">
                        <span className="font-semibold text-lg text-indigo-800">
                          Pro
                        </span>
                        <span className="text-xl font-bold text-gray-700">
                          ₦
                          {(
                            (selectedPlanAmount ||
                              planConfigs[ALLOWED_UPGRADE_PLAN].amount) *
                            upgradeMonths
                          ).toLocaleString()}
                        </span>
                        <span className="uppercase mt-1 text-xs text-indigo-800 font-bold opacity-75">
                          Upgrade • {upgradeMonths}{" "}
                          {upgradeMonths === 1 ? "month" : "months"}
                        </span>
                      </div>
                      <div className="w-full flex flex-col items-center">
                        <label className="text-gray-700 font-medium mb-1">
                          Select number of months to pay for:
                        </label>
                        <select
                          value={upgradeMonths}
                          className="border px-4 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 text-lg"
                          style={{ minWidth: 120 }}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setUpgradeMonths(value);
                          }}
                        >
                          {[1, 2, 3, 6, 12].map((m) => (
                            <option value={m} key={`upgrade-${m}`}>
                              {m} {m === 1 ? "month" : "months"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center my-2 gap-3 w-full">
                      <div className="flex flex-col border rounded-lg px-5 py-4 bg-blue-50 border-blue-200 w-full">
                        <span className="font-semibold text-lg text-blue-800">
                          {planConfigs[selectedPlan]?.name || planDisplayName}
                        </span>
                        <span className="text-xl font-bold text-gray-700">
                          ₦
                          {(
                            planConfigs[selectedPlan]?.amount * renewMonths
                          ).toLocaleString()}
                        </span>
                        <span className="uppercase mt-1 text-xs text-blue-500 font-bold opacity-75">
                          Renewal &bull; {renewMonths}{" "}
                          {renewMonths === 1 ? "month" : "months"}
                        </span>
                      </div>
                      {/* Month Selector */}
                      <div className="w-full flex flex-col items-center">
                        <label
                          htmlFor="renewMonthsSelect"
                          className="text-gray-700 font-medium mb-1"
                        >
                          Select number of months to pay for:
                        </label>
                        <select
                          id="renewMonthsSelect"
                          value={renewMonths}
                          className="border px-4 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 text-lg"
                          style={{ minWidth: 120 }}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setRenewMonths(value);
                          }}
                        >
                          {[1, 2, 3, 6, 12].map((m) => (
                            <option value={m} key={m}>
                              {m} {m === 1 ? "month" : "months"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="mt-7">
                    {selectedPlan ? (
                      <Suspense
                        fallback={
                          <button
                            className="w-full bg-blue-500 text-white py-3 mt-2 rounded-lg"
                            disabled
                          >
                            Loading Paystack...
                          </button>
                        }
                      >
                        <PaystackButton
                          {...paystackConfig}
                          text={
                            paystackLoading
                              ? "Processing..."
                              : isRenewal
                                ? `Pay ₦${selectedPlanTotalAmount.toLocaleString()} to Renew`
                                : `Pay ₦${selectedPlanTotalAmount.toLocaleString()} to Upgrade`
                          }
                          className={`w-full py-3 rounded-lg bg-gradient-to-r from-blue-700 to-fuchsia-600 text-white shadow-md hover:from-fuchsia-700 hover:to-blue-800 font-bold transition-all text-lg`}
                          disabled={paystackLoading || !pendingAmount}
                          onSuccess={handlePaystackSuccess}
                          onClose={handlePaystackClose}
                        />
                      </Suspense>
                    ) : (
                      <button
                        className="w-full py-3 rounded-lg font-bold bg-gray-300 text-gray-600 cursor-not-allowed"
                        disabled
                      >
                        Select a plan to continue
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Information */}
            <div className="text-sm text-gray-600 space-y-2 text-center">
              <p>
                Need help? Contact our support team at{" "}
                <a
                  href="mailto:support@01pos.net"
                  className="text-blue-600 hover:underline"
                >
                  support@01pos.net
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Renew;
